import type { Slide, Deck } from '@/entities/deck'
import type { DeepReadonly } from 'vue'

export default function usePreloadPdfImages({
  deck,
  currentSlideIndex,
  isEmbedded,
}: {
  deck: DeepReadonly<Ref<Deck>>
  currentSlideIndex: Readonly<Ref<number>>
  isEmbedded: Readonly<Ref<boolean>>
}) {
  const preloadImage = (imageUrl: string) => {
    return new Promise(resolve => {
      const img = new Image()
      img.onload = resolve
      img.src = imageUrl
    })
  }

  watch(
    () => currentSlideIndex.value,
    () => {
      const nextSetOfSlides = deck.value.slides.slice(currentSlideIndex.value + 1, currentSlideIndex.value + 11)
      preloadSlideImages(nextSetOfSlides)
    }
  )

  onMounted(() => {
    // if embedded we only want to load first slide, then preload the rest
    // if not embedded, we can go ahead and load them all
    if (!isEmbedded.value) preloadSlideImages(deck.value.slides)
  })

  /**
   * pre-load the images inside PDF / Image slides so that they'll be cached when a user is ready to view them
   * @param {Array} slides - array of slide objects
   */
  const preloadSlideImages = async (slides: DeepReadonly<Slide[]>) => {
    const mediaType = {
      image: 'I',
      pdf: 'P',
    }

    for (const slide of slides) {
      let imageUrl
      if (slide.type === mediaType['image'] && slide.file?.url) imageUrl = `${slide.file.url}`
      if (slide.type === mediaType['pdf'] && slide.image?.url) imageUrl = `${slide.image.url}`

      if (imageUrl) await preloadImage(imageUrl)
    }
  }
}
